/// <amd-module name="Pipeline/Analytics/Common/Stores/PipelineReportBaseStore" />
import { extractODataError } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import { ConfigurationUtility } from "Analytics/Shared/TrendChart/Common/ConfigurationUtility";
import { PipelineReportBaseActions } from "Pipeline/Analytics/Common/Actions/PipelineReportBaseActions";
import { IPipelineOutcomeReportConfiguration } from "Pipeline/Analytics/Common/Types";
import { Store } from "VSS/Reflux/Store";

export interface IPipelineReportBaseState<D extends IPipelineReportBaseData> {
    confValues?: IPipelineOutcomeReportConfiguration;
    errorText?: string;
    data?: D;
}

export interface IPipelineReportBaseData {}

export abstract class PipelineReportBaseStore<
    P extends PipelineReportBaseActions<D>,
    S extends IPipelineReportBaseState<D>,
    D extends IPipelineReportBaseData
> extends Store {
    protected _state: S;

    constructor(protected _actions: P) {
        super("");
        this._initialize();
    }

    public getState(): S {
        return this._state;
    }

    public dispose(): void {
        this._actions.updateConfigurationValuesAction.removeListener(this._onUpdateConfiguration);
        this._actions.updateDataAction.removeListener(this._onUpdateData);
        this._actions.onError.removeListener(this._onError);
    }

    protected _initialize(): void {
        this._state = {} as S;
        this._actions.updateConfigurationValuesAction.addListener(this._onUpdateConfiguration);
        this._actions.updateDataAction.addListener(this._onUpdateData);
        this._actions.onError.addListener(this._onError);
    }

    protected _onError = (error: Error) => {
        const errorText = extractODataError(error);
        this._state.errorText = errorText;
        this.emitChanged();
    };

    protected _onUpdateConfiguration = (confValues: IPipelineOutcomeReportConfiguration) => {
        if (
            this._state.confValues &&
            ConfigurationUtility.areConfValuesEqual(this._state.confValues, confValues) &&
            this._state.confValues.stage === confValues.stage
        ) {
            return;
        }
        this._state.errorText = undefined;
        this._state.data = undefined;
        this._state.confValues = { ...confValues };
        this.emitChanged();
    };

    protected _onUpdateData = (cardData: D) => {
        if (cardData) {
            this._state.errorText = undefined;
            this._state.data = cardData;
            this.emitChanged();
        }
    };
}
